import moment from 'moment';
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import microsoftTeamsIcon from '~/assets/public/microsoft-teams.svg';
import { Checkbox, Dropdown, Icon, Input, Tooltip } from '~/components';
import { colors, weights } from '~/styles';
import { dateFormats } from '~/utils';

const Control = styled.button`
  display: flex;
  align-items: center;
  width: 100%;
  height: auto;
  padding: 0.625rem 0.75rem;
  color: ${colors.black};
  font-weight: ${weights.normal};
  text-align: left;
  background-color: ${colors.white};
  border: solid 1px ${colors.grey10};
  border-radius: 0.3125rem;

  &:hover {
    color: ${colors.black};
    background-color: ${colors.white};
    border-color: ${colors.grey25};
  }
`;

const Summary = styled.span`
  flex: 1;
`;

const Menu = styled(Dropdown.Menu)`
  width: 100%;
`;

const Filter = styled.div`
  padding: 0.5rem 0.875rem;
  background-color: ${colors.grey5};
  border-bottom: solid 1px ${colors.grey10};
`;

const Options = styled.div`
  max-height: 15rem;
  overflow-y: auto;
`;

const Option = styled.div`
  padding: 0.375rem 0.75rem;

  label > div {
    width: 1.125rem;
    height: 1.125rem;
    font-size: 0.625rem;
  }

  &:hover {
    background-color: ${colors.grey5};
  }
`;

const OptionRow = styled.div`
  display: flex;
  align-items: center;
`;

const OptionLabel = styled.div`
  flex: 1;
  min-width: 0;
`;

const MicrosoftTeamsIcon = styled.img`
  display: block;
  flex-shrink: 0;
  width: 1rem;
  height: 1rem;
  margin-left: 0.5rem;
`;

const Empty = styled.div`
  padding: 0.75rem;
  color: ${colors.grey40};
`;

const NOT_LINKED =
  'Not linked to Microsoft Teams. Members link their account from a personal chat with Ruddr in Teams.';

function linkMessage({ account, tenantDomain, linkedAt }) {
  const date = moment(linkedAt).format(dateFormats.shortDate);
  const tenant = tenantDomain ? ` (${tenantDomain})` : '';
  return account ? `Linked as ${account}${tenant} on ${date}.` : `Linked${tenant} on ${date}.`;
}

export default function MicrosoftTeamsMembers({ members, removedIds, onChange }) {
  const [term, setTerm] = useState('');

  const removed = useMemo(() => new Set(removedIds), [removedIds]);

  const linked = members.filter((member) => member.link && !removed.has(member.id)).length;

  const visible = useMemo(() => {
    const search = term.trim().toLowerCase();
    if (!search) return members;
    return members.filter(
      (member) => member.name?.toLowerCase().includes(search) || member.email?.toLowerCase().includes(search),
    );
  }, [members, term]);

  const toggle = (id) => {
    const next = new Set(removed);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange([...next]);
  };

  return (
    <Dropdown>
      {({ setIsOpen, isOpen }) => (
        <>
          <Dropdown.Trigger>
            <Control type="button" onClick={() => setIsOpen(!isOpen)} data-testid="ms-teams-members-control">
              <Summary>{`${linked} of ${members.length} members linked`}</Summary>
              <Icon icon={isOpen ? 'angle-up' : 'angle-down'} />
            </Control>
          </Dropdown.Trigger>

          <Menu>
            <Filter>
              <Input
                type="search"
                wait={300}
                placeholder="Search members"
                value={term}
                onChange={(event) => setTerm(event.target.value)}
                data-testid="ms-teams-members-search"
              />
            </Filter>
            {visible.length === 0 ? (
              <Empty>No members match that search.</Empty>
            ) : (
              <Options>
                {visible.map((member) => {
                  const label = member.email ? `${member.name} (${member.email})` : member.name;
                  const subText = member.isActive === false ? 'Inactive' : undefined;
                  return (
                    <Option key={member.id} data-testid={`ms-teams-member-${member.id}`}>
                      {member.link ? (
                        <OptionRow>
                          <OptionLabel>
                            <Checkbox
                              checked={!removed.has(member.id)}
                              onChange={() => toggle(member.id)}
                              label={label}
                              subText={subText}
                              data-testid={`ms-teams-member-checkbox-${member.id}`}
                            />
                          </OptionLabel>
                          <Tooltip message={linkMessage(member.link)}>
                            <MicrosoftTeamsIcon
                              src={microsoftTeamsIcon}
                              alt="Microsoft Teams"
                              data-testid={`ms-teams-member-logo-${member.id}`}
                            />
                          </Tooltip>
                        </OptionRow>
                      ) : (
                        <Tooltip message={NOT_LINKED}>
                          <Checkbox
                            checked={false}
                            disabled
                            onChange={() => {}}
                            label={label}
                            subText={subText}
                            data-testid={`ms-teams-member-checkbox-${member.id}`}
                          />
                        </Tooltip>
                      )}
                    </Option>
                  );
                })}
              </Options>
            )}
          </Menu>
        </>
      )}
    </Dropdown>
  );
}
